import type { ReactNode } from "react";
import { PanelLeftClose, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The shell's top bar: brand, search and the account, across the full width.
 *
 * The workspace is not here: it names the rail's contents, so it sits at the
 * rail's head where what it scopes is in view.
 *
 * The brand block is sized to the rail below it so the two share an edge, and
 * the search takes the middle as its own object rather than hiding in a corner:
 * it is the one control every screen can use.
 */
export function AppTopBar({
  logoSrc,
  iconSrc,
  logoAlt,
  leadingWidth,
  searchActive,
  searchShortcut,
  onSearch,
  actions,
  railCollapsed,
  onToggleRail,
}: {
  logoSrc: string;
  /** Compact mark for the folded rail, where the wordmark will not fit. */
  iconSrc?: string;
  logoAlt: string;
  /** Width class matching the rail, so the brand lines up with it. */
  leadingWidth?: string;
  searchActive?: boolean;
  searchShortcut?: string;
  onSearch?: () => void;
  actions?: ReactNode;
  /** The rail's fold control. It lives here, over the rail's own edge, so the
   *  rail can start at its first destination instead of at a row that holds
   *  nothing else. */
  railCollapsed?: boolean;
  onToggleRail?: () => void;
}) {
  return (
    <header className="flex h-[56px] shrink-0 items-center gap-3 pr-3">
      <div className={cn("relative flex shrink-0 items-center justify-center", leadingWidth)}>
        <img
          src={railCollapsed ? (iconSrc ?? logoSrc) : logoSrc}
          alt={logoAlt}
          className={cn("w-auto object-contain", railCollapsed ? "h-8 max-w-[40px]" : "h-[52px] max-w-[210px]")}
        />
        {/* Only while the rail is open. Folded, the control belongs among the
            icons it folded — there is no name beside it to sit next to here. */}
        {onToggleRail && !railCollapsed && (
          <button
            onClick={onToggleRail}
            aria-label="Colapsar menú"
            className="absolute right-0 flex h-7 w-7 items-center justify-center rounded-md text-mute transition-colors hover:bg-black/[0.05] hover:text-ink-900 dark:hover:bg-white/[0.07]"
          >
            <PanelLeftClose className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="flex min-w-0 flex-1 justify-start">
        {onSearch && (
          <button
            onClick={onSearch}
            className={cn(
              "group flex h-10 w-full max-w-[620px] items-center gap-3 rounded-full px-4 text-[13px] transition-colors",
              "bg-panel shadow-[0_1px_3px_rgb(15_23_42/0.08)] hover:shadow-[0_1px_4px_rgb(15_23_42/0.14)]",
              searchActive ? "text-ink-800" : "text-mute",
            )}
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left">Buscar conversación, contacto o acción…</span>
            <kbd className="shrink-0 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
              {searchShortcut}
            </kbd>
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">{actions}</div>
    </header>
  );
}
